// The edge strip's colour (docs/prd/2026-11-04-edge-fill.md).
//
// iPhone Safari (iOS 26) fills the band behind its toolbar from the fixed element it
// finds at the midpoint of the viewport's bottom edge, and draws nothing of the page
// there; with no such element the band is a glass over the page's own content. The
// strip (`.edge-strip`, in the layout from the first paint) is that element: 12 px,
// full width, fixed to the foot, above the page. Its colour is the page's at the foot,
// so the band reads as the page ending at the toolbar, as raggededge.com's does.
//
// The colour is the frontmost full-width element with an opaque background on the line
// just above the strip: a section, the footer, the cookie banner, the open menu (the fade's
// sheet and solid cover excepted: under a change the band keeps the page's colour, as
// Ragged Edge's). Anything narrower than the screen (a card, inset from the edges by its
// gutter) is passed over, so a card scrolling by never tints the band.
// It is read on mount (the first load, and each page change, under the cover, as Ragged
// Edge's changes at the swap), on scroll and on resize (the toolbar collapsing moves the
// foot), and when the header's classes change (the menu opening and closing). Safari
// follows the strip's colour within about four frames; the CSS transition is the 0.3 s
// tween Ragged Edge give their body colour.

const BLEED = 2; // px: an element this close to the viewport's width is the page; narrower ones (cards at 92%) are content
const ABOVE = 2; // px above the strip where the page is read

export function mountEdge({ signal }) {
  const strip = document.querySelector(".edge-strip");
  if (!strip || getComputedStyle(strip).display === "none") return;

  let queued = 0;
  const update = () => {
    queued = 0;
    const colour = colourAtFoot(strip);
    if (colour && strip.style.backgroundColor !== colour) strip.style.backgroundColor = colour;
  };
  const schedule = () => {
    if (!queued) queued = requestAnimationFrame(update);
  };

  update();
  window.addEventListener("scroll", schedule, { passive: true, signal });
  window.addEventListener("resize", schedule, { signal });
  window.visualViewport?.addEventListener("resize", schedule, { signal });
  document.addEventListener("transitionend", schedule, { signal });

  const header = document.querySelector(".nav");
  const observer = header ? new MutationObserver(schedule) : null;
  observer?.observe(header, { attributes: true, attributeFilter: ["class"], subtree: true });
  signal.addEventListener("abort", () => {
    observer?.disconnect();
    if (queued) cancelAnimationFrame(queued);
  });
}

function colourAtFoot(strip) {
  const width = document.documentElement.clientWidth;
  const y = strip.getBoundingClientRect().top - ABOVE;
  for (const element of document.elementsFromPoint(width / 2, y)) {
    if (element === strip || element === document.documentElement) continue;
    if (element.closest(".feel-layer")) continue; // the fade's sheet is not the page
    const style = getComputedStyle(element);
    if (!isOpaque(style.backgroundColor) || parseFloat(style.opacity) < 0.99) continue;
    if (element.getBoundingClientRect().width < width - BLEED) continue;
    return style.backgroundColor;
  }
  return getComputedStyle(document.body).backgroundColor;
}

function isOpaque(colour) {
  const match = colour.match(/rgba?\(([^)]+)\)/);
  if (!match) return false;
  const parts = match[1].split(/[\s,/]+/).filter(Boolean);
  return parts.length < 4 || parseFloat(parts[3]) >= 0.99;
}
